import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Pencil, X } from "lucide-react";

interface OptionButtonsProps {
  options: string[];
  onSelect: (value: string) => void;
  disabled?: boolean;
  isWriteOwnExpanded: boolean;
  onExpandWriteOwn: () => void;
  writeOwnValue: string;
  onWriteOwnChange: (value: string) => void;
  onWriteOwnSubmit: () => void;
  onCollapseWriteOwn: () => void;
}

const OptionButtons = ({
  options,
  onSelect,
  disabled = false,
  isWriteOwnExpanded,
  onExpandWriteOwn,
  writeOwnValue,
  onWriteOwnChange,
  onWriteOwnSubmit,
  onCollapseWriteOwn,
}: OptionButtonsProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (writeOwnValue.trim()) {
        onWriteOwnSubmit();
      }
    }
    if (e.key === "Escape") {
      onCollapseWriteOwn();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    onWriteOwnChange(textarea.value);
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  return (
    <div className="flex flex-col gap-2 pl-0 md:pl-2 fade-in-up">
      {/* Option buttons */}
      <div className="flex flex-wrap gap-2">
        {options.map((option, index) => (
          <Button
            key={index}
            variant="outline"
            className="rounded-xl text-sm whitespace-normal text-left h-auto py-2 px-4 max-w-full"
            onClick={() => onSelect(option)}
            disabled={disabled}
          >
            {option}
          </Button>
        ))}
      </div>

      {/* Write my own: collapsed button or expanded textarea */}
      {!isWriteOwnExpanded ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start text-muted-foreground text-xs gap-1.5"
          onClick={onExpandWriteOwn}
          disabled={disabled}
        >
          <Pencil className="h-3.5 w-3.5" />
          Write my own
        </Button>
      ) : (
        <div className="flex gap-2 items-end">
          <textarea
            value={writeOwnValue}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your own response..."
            rows={1}
            autoFocus
            style={{ minHeight: "40px", maxHeight: "120px" }}
            className="flex-1 resize-none rounded-xl border border-border bg-muted/30 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 overflow-y-auto"
            disabled={disabled}
          />
          <Button
            onClick={onWriteOwnSubmit}
            disabled={!writeOwnValue.trim() || disabled}
            size="icon"
            className="h-10 w-10 shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCollapseWriteOwn}
            className="h-10 w-10 shrink-0 text-muted-foreground"
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default OptionButtons;
